import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  Chip,
  Button,
  Skeleton,
  Grid,
  useTheme,
  useMediaQuery,
  Modal,
  CircularProgress,
} from "@mui/material";
import { RootState } from "../../services/redux/store";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import PDFViewer from "../../components/ui/PDFViewer";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { Close } from "@mui/icons-material";
import CustomDivider from "../../components/common/CustomDivider";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import { isDeviceTypeMobile } from "../../utils/helpers";

// --- Interfaces ---
interface MandatoryDocument {
  id: string;
  fileName: string;
  description: string;
  status: "Uploaded" | "Pending";
  url?: string;
}

const ViewBulkDocumentsView = () => {
  const location = useLocation();
  const tabIndex: number = location?.state?.tabIndex || 0;
  const formUserData: any = useSelector(
    (state: RootState) => state.stepper.formData
  );
  const applicationNumber = useSelector(
    (state: RootState) => state.stepper.trackingId
  );
  const [isLoading, setIsLoading] = useState(false);
  const [bulkUploadDocumentData, setBulkUploadDocumentData] = useState<
    MandatoryDocument[]
  >([]);
  const [viewDocUrl, setViewDocUrl] = useState<string>("");
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<MandatoryDocument | null>(null);

  const breadcrumbItems = isXsOrSm
    ? [
      {
        label: `${formUserData.courseName || ""} [#${applicationNumber || ""
          }]`,
        path: "",
      },
      { label: "Bulk Documents" },
    ]
    : [
      { label: "Dashboard", path: "/application/existing-application" },
      {
        label: `${formUserData.courseName || ""} [#${applicationNumber || ""
          }]`,
        path: "",
      },
      { label: "Bulk Documents" },
    ];

  const getViewBulkDocumentsViaAPI = async () => {
    try {
      setIsLoading(true);
      const response = await eligibilityCheckServices.getViewBulkDocuments(
        applicationNumber
      );

      setBulkUploadDocumentData(response?.data?.data);
    } catch (error: any) {
      console.log(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getViewBulkDocumentsViaAPI();
  }, [tabIndex, applicationNumber]);

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedDocument(null);
  };

  const handlePreviewDocument = async (doc: any) => {
    setIsModalLoading(true);
    const payload = {
      key: doc.url,
    };

    try {
      const response = await eligibilityCheckServices.previewDocument(payload);
      if (response?.status == 200) {
        setViewDocUrl(response?.data?.data?.downloadUrl);
        setSelectedDocument(doc);
        setIsViewModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching document", error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleShowImageMList = () => {
    const isPdf = selectedDocument?.fileName?.toLowerCase().endsWith(".pdf") || viewDocUrl?.toLowerCase().includes(".pdf");

    return (
      <>
        <div>
          <Grid container spacing={2} sx={{ paddingBottom: "20px" }}>
            <Grid item xs={12}>
              <div
                className="image-container"
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  minWidth: isXsOrSm ? "300px" : "100%",
                  height: isXsOrSm ? "320px" : "80vh",
                  overflow: "auto",
                  marginTop: "39px",
                  position: "relative",
                  backgroundColor: "#fff",
                }}
              >
                {isModalLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <CircularProgress size={40} sx={{ color: "#00b3b5" }} />
                  </Box>
                ) : (
                  <>
                    {isPdf ? (
                      <PDFViewer pdfUrl={viewDocUrl} />
                    ) : (
                      <img
                        src={viewDocUrl}
                        alt="document preview"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    );
  };

  // Skeleton loader for list items
  const renderSkeleton = () => (
    <List sx={{ p: 0 }}>
      {[...Array(bulkUploadDocumentData.length || 4)].map((_, idx) => (
        <ListItem
          key={idx}
          sx={{
            mb: 1,
            bgcolor: "#ffffff",
            border: "1px solid #eee",
            borderRadius: "8px",
            py: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="rectangular" width={80} height={24} />
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      <Grid item xs={12} mb={2} mt={isDeviceTypeMobile(isXsOrSm) ? 10 : 0}>
        <Breadcrumbs items={breadcrumbItems} />
      </Grid>

      <Grid
        container
        sx={{ mb: 2, backgroundColor: "#fff" }}
        p={isXsOrSm ? 2 : 3}
      >
        <Grid item xs={12} spacing={2}>
          <Typography
            sx={{ color: "#5d586c !important" }}
            className="section-heading"
          >
            Bulk Documents
          </Typography>

          {isXsOrSm && <CustomDivider />}

          <Box sx={{ mt: "25px !important" }}>
            {isLoading ? (
              renderSkeleton()
            ) : (
              <List sx={{ p: 0, alignItems: "center" }}>
                {bulkUploadDocumentData?.length == 0 ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "15px",
                        color: "#6c6c6cff",
                        height: "300px",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      No documents uploaded!
                    </Typography>
                  </>
                ) : (
                  <>
                    {bulkUploadDocumentData.map((doc) => (
                      <ListItem
                        key={doc.id}
                        sx={{
                          mb: 1,
                          bgcolor: "#ffffff",
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          py: 1.5,
                          px: 2,
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: { xs: "stretch", md: "center" },
                        }}
                      >
                        {/* First row: file name and chip (and button for md+) */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <TruncatedTextWithTooltip
                            className="breadcrumbs"
                            text={String(doc.fileName || "")}
                            // limit={isXsOrSm ? 25 : 45}
                            style={{
                              my: 0,
                              flexGrow: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontWeight: 600,
                              fontSize: "15px",
                              color: "#5d586c",
                            }}
                          />

                          <Chip
                            label={doc.status}
                            size="small"
                            sx={{
                              backgroundColor: "#28C76F29",
                              color: "#28C76F",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              borderRadius: "4px",
                              height: "24px",
                              minWidth: "auto",
                              ml: 2,
                              textTransform: "capitalize",
                            }}
                          />

                          {/* Button in same row for md+, hidden for xs/sm */}
                          <Box
                            sx={{
                              display: { xs: "none", md: "flex" },
                              ml: 2,
                            }}
                          >
                            <Button
                              variant="text"
                              onClick={() => handlePreviewDocument(doc)}
                              sx={{
                                textTransform: "none",
                                color: "#00b3b5",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                minWidth: "auto",
                                px: 1,
                                py: 0.5,
                              }}
                              startIcon={
                                <VisibilityIcon sx={{ fontSize: "1rem" }} />
                              }
                            >
                              View
                            </Button>
                          </Box>
                        </Box>

                        {/* Second row: button for xs/sm only */}
                        <Box
                          sx={{
                            display: { xs: "flex", md: "none" },
                            width: "100%",
                            mt: 1,
                          }}
                        >
                          <Button
                            variant="text"
                            onClick={() => handlePreviewDocument(doc)}
                            sx={{
                              textTransform: "none",
                              color: "#00b3b5",
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              minWidth: "auto",
                              px: 1,
                              py: 0.5,
                            }}
                            startIcon={
                              <VisibilityIcon sx={{ fontSize: "1rem" }} />
                            }
                          >
                            View
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </>
                )}
              </List>
            )}
          </Box>
        </Grid>
      </Grid>

      <Modal
        open={isViewModalOpen}
        onClose={() => {
          handleCloseViewModal();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isXsOrSm ? "300px" : "80%",
            minHeight: isXsOrSm ? "380px" : "",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Close
            onClick={() => {
              setIsViewModalOpen(false);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          />
          {handleShowImageMList()}
        </Box>
      </Modal>

    </>
  );
};

export default ViewBulkDocumentsView;
